import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChange } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let unsubscribe = () => {};

    try {
      unsubscribe = onAuthStateChange(async (user) => {
        if (!isMounted) return;
        
        setUser(user);
        setLoading(false);

        if (user) {
          try {
            const { checkUserAuthorization } = await import('../services/authService');
            const authorized = await checkUserAuthorization(user.uid);
            if (isMounted) {
              setIsAuthorized(authorized);
            }
          } catch (error) {
            console.error('Authorization check error:', error);
            if (isMounted) {
              setIsAuthorized(false);
              setAuthError('Failed to check authorization');
            }
          }
        } else {
          if (isMounted) {
            setIsAuthorized(false);
          }
        }
      });
    } catch (error) {
      console.error('Auth provider error:', error);
      if (isMounted) {
        setLoading(false);
        setAuthError('Failed to initialize authentication');
      }
    }

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const value = {
    user,
    loading,
    isAuthorized,
    authError,
    setUser,
    setIsAuthorized
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};